/**
 * FloatingAIChat — chat flotante con IA para Institutional Admin.
 * - Botón flotante fijo (esquina inferior derecha).
 * - Ventana expandible con conversación multi-turn.
 * - Solo soporte de USO de QHUB.BUDDY (la IA nunca muestra código).
 * - Botón "Escalar al Super Admin" que envía todo el hilo como mensaje formal.
 */
import React, { useState, useRef, useEffect } from "react";
import axios from "axios";
import { API } from "@/App";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import {
  MessageCircle, X, Send, Loader2, Bot, User as UserIcon,
  AlertTriangle, CheckCircle2, RefreshCw,
} from "lucide-react";

const WELCOME = "¡Hola! Soy el Asistente de Soporte de QHUB.BUDDY. Puedo ayudarte con dudas de uso del sistema, dónde encontrar cosas y cómo funcionan las herramientas del dashboard. Si el tema requiere apoyo humano, te ayudaré a escalarlo al Super Admin. ¿En qué te ayudo?";

export default function FloatingAIChat() {
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([{ role: "assistant", content: WELCOME }]);
  const [input, setInput] = useState("");
  const [sending, setSending] = useState(false);
  const [sessionId, setSessionId] = useState(null);
  const [suggestEscalate, setSuggestEscalate] = useState(false);
  const [suggestedCategory, setSuggestedCategory] = useState("support");
  const [escalating, setEscalating] = useState(false);
  const [escalated, setEscalated] = useState(false);
  const scrollRef = useRef(null);

  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages, open]);

  const send = async () => {
    const text = input.trim();
    if (!text || sending || escalated) return;
    setInput("");
    setMessages((prev) => [...prev, { role: "user", content: text }]);
    setSending(true);
    try {
      const res = await axios.post(
        `${API}/support-bot/chat`,
        { session_id: sessionId, message: text },
        { withCredentials: true, timeout: 30000 },
      );
      const data = res.data || {};
      setSessionId(data.session_id || sessionId);
      setSuggestEscalate(Boolean(data.suggest_escalate));
      setSuggestedCategory(data.suggested_category || "support");
      setMessages((prev) => [...prev, { role: "assistant", content: data.reply || "…" }]);
    } catch (e) {
      const detail = e?.response?.data?.detail || "No se pudo obtener respuesta del asistente.";
      setMessages((prev) => [...prev, { role: "assistant", content: `⚠️ ${detail}` }]);
      toast.error(detail);
    } finally {
      setSending(false);
    }
  };

  const escalate = async () => {
    if (!sessionId || escalating || escalated) return;
    setEscalating(true);
    try {
      await axios.post(
        `${API}/support-bot/escalate`,
        { session_id: sessionId, category: suggestedCategory, priority: "medium" },
        { withCredentials: true, timeout: 15000 },
      );
      setEscalated(true);
      toast.success("Caso escalado al Super Admin ✅");
      setMessages((prev) => [
        ...prev,
        {
          role: "system",
          content:
            "Este caso fue enviado al Super Admin junto con todo el historial. Te contactará por Mensajería lo antes posible.",
        },
      ]);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "No se pudo escalar el caso.");
    } finally {
      setEscalating(false);
    }
  };

  const resetChat = async () => {
    try {
      await axios.post(`${API}/support-bot/reset`, {}, { withCredentials: true });
    } catch (_) {
      // best-effort
    }
    setSessionId(null);
    setSuggestEscalate(false);
    setSuggestedCategory("support");
    setEscalated(false);
    setMessages([{ role: "assistant", content: WELCOME }]);
  };

  const onKeyDown = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      send();
    }
  };

  return (
    <>
      {/* Floating trigger button */}
      {!open && (
        <button
          onClick={() => setOpen(true)}
          data-testid="floating-ai-chat-open-btn"
          className="fixed bottom-6 right-6 z-40 flex items-center gap-2 px-5 py-3 rounded-full bg-gradient-to-r from-[#419cca] to-[#8b5cf6] text-white shadow-2xl hover:shadow-[0_10px_40px_rgba(139,92,246,0.5)] transition-all hover:scale-105 group"
          aria-label="Abrir asistente de soporte"
        >
          <div className="relative">
            <Bot className="w-6 h-6" />
            <span className="absolute -top-1 -right-1 w-3 h-3 bg-green-400 rounded-full ring-2 ring-white animate-pulse" />
          </div>
          <span className="font-medium text-sm hidden sm:inline">Soporte IA</span>
        </button>
      )}

      {/* Chat panel */}
      {open && (
        <div
          className="fixed bottom-6 right-6 z-40 w-[min(400px,calc(100vw-2rem))] h-[min(600px,calc(100vh-3rem))] bg-white rounded-2xl shadow-2xl border border-gray-100 flex flex-col overflow-hidden animate-in slide-in-from-bottom-4 duration-200"
          data-testid="floating-ai-chat-panel"
        >
          {/* Header */}
          <div className="flex items-center justify-between p-3 bg-gradient-to-r from-[#419cca] to-[#8b5cf6] text-white">
            <div className="flex items-center gap-2">
              <div className="p-1.5 bg-white/20 rounded-lg">
                <Bot className="w-5 h-5" />
              </div>
              <div>
                <div className="font-semibold text-sm leading-tight">Soporte QHUB IA</div>
                <div className="text-[11px] opacity-90 leading-tight">
                  Nivel 1 · Escalamiento a humano disponible
                </div>
              </div>
            </div>
            <div className="flex items-center gap-1">
              <button
                onClick={resetChat}
                className="p-1.5 rounded-md hover:bg-white/20 transition-colors"
                title="Reiniciar conversación"
                data-testid="floating-ai-chat-reset-btn"
              >
                <RefreshCw className="w-4 h-4" />
              </button>
              <button
                onClick={() => setOpen(false)}
                className="p-1.5 rounded-md hover:bg-white/20 transition-colors"
                data-testid="floating-ai-chat-close-btn"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
          </div>

          {/* Messages */}
          <div
            ref={scrollRef}
            className="flex-1 overflow-y-auto p-4 space-y-3 bg-gray-50"
            data-testid="floating-ai-chat-messages"
          >
            {messages.map((m, i) => {
              if (m.role === "system") {
                return (
                  <div
                    key={i}
                    className="flex items-start gap-2 text-xs text-green-700 bg-green-50 border border-green-100 rounded-lg p-2.5"
                  >
                    <CheckCircle2 className="w-4 h-4 mt-0.5 shrink-0" />
                    <p>{m.content}</p>
                  </div>
                );
              }
              const isUser = m.role === "user";
              return (
                <div
                  key={i}
                  className={`flex gap-2 ${isUser ? "flex-row-reverse" : "flex-row"}`}
                >
                  <div
                    className={`shrink-0 w-7 h-7 rounded-full flex items-center justify-center text-white ${
                      isUser ? "bg-[#419cca]" : "bg-gradient-to-br from-purple-500 to-pink-500"
                    }`}
                  >
                    {isUser ? <UserIcon className="w-3.5 h-3.5" /> : <Bot className="w-3.5 h-3.5" />}
                  </div>
                  <div
                    className={`max-w-[80%] rounded-2xl px-3 py-2 text-sm whitespace-pre-wrap break-words ${
                      isUser
                        ? "bg-[#419cca] text-white rounded-tr-sm"
                        : "bg-white text-gray-800 border border-gray-100 rounded-tl-sm"
                    }`}
                  >
                    {m.content}
                  </div>
                </div>
              );
            })}
            {sending && (
              <div className="flex gap-2">
                <div className="shrink-0 w-7 h-7 rounded-full bg-gradient-to-br from-purple-500 to-pink-500 flex items-center justify-center">
                  <Bot className="w-3.5 h-3.5 text-white" />
                </div>
                <div className="bg-white border border-gray-100 rounded-2xl rounded-tl-sm px-3 py-2 flex gap-1">
                  <span className="w-1.5 h-1.5 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: "0ms" }} />
                  <span className="w-1.5 h-1.5 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: "150ms" }} />
                  <span className="w-1.5 h-1.5 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: "300ms" }} />
                </div>
              </div>
            )}
          </div>

          {/* Escalation banner */}
          {suggestEscalate && !escalated && (
            <div className="px-3 py-2 bg-amber-50 border-t border-amber-100 flex items-center gap-2">
              <AlertTriangle className="w-4 h-4 text-amber-600 shrink-0" />
              <p className="text-xs text-amber-800 flex-1">
                Este caso puede requerir apoyo humano.
              </p>
              <Button
                size="sm"
                onClick={escalate}
                disabled={escalating}
                className="h-7 text-xs bg-amber-600 hover:bg-amber-700 text-white gap-1"
                data-testid="floating-ai-chat-escalate-btn"
              >
                {escalating ? (
                  <Loader2 className="w-3 h-3 animate-spin" />
                ) : (
                  <AlertTriangle className="w-3 h-3" />
                )}
                Escalar al Super Admin
              </Button>
            </div>
          )}

          {/* Input */}
          <div className="border-t border-gray-100 p-3 bg-white">
            {escalated ? (
              <div className="text-center text-xs text-gray-500 py-2">
                Conversación escalada. Presiona{" "}
                <button
                  onClick={resetChat}
                  className="text-[#419cca] underline font-medium"
                  data-testid="floating-ai-chat-new-btn"
                >
                  nueva consulta
                </button>{" "}
                para empezar de nuevo.
              </div>
            ) : (
              <div className="flex gap-2 items-end">
                <Textarea
                  value={input}
                  onChange={(e) => setInput(e.target.value)}
                  onKeyDown={onKeyDown}
                  placeholder="Escribe tu duda… (Enter para enviar)"
                  rows={1}
                  maxLength={2000}
                  disabled={sending}
                  className="resize-none text-sm min-h-[40px] max-h-[120px]"
                  data-testid="floating-ai-chat-input"
                />
                <Button
                  onClick={send}
                  disabled={sending || !input.trim()}
                  size="sm"
                  className="h-10 w-10 p-0 shrink-0 bg-gradient-to-r from-[#419cca] to-[#8b5cf6] text-white"
                  data-testid="floating-ai-chat-send-btn"
                >
                  {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                </Button>
              </div>
            )}
          </div>
        </div>
      )}
    </>
  );
}
